import { z } from 'zod';
import { AuditSchema, IdSchema, InstantSchema } from './common.ts';

/**
 * One person's work on one job, between a clock-in and a clock-out
 * (SCRUM-142, P8-07).
 *
 * `docs/features/13-later-modules.md`: "Clock in/out with job and cost
 * code, shifts, breaks, approval (feeds 'Unapproved Shifts' action item
 * and 'N users clocked in'), time sheets, labor cost to job costing,
 * geolocation optional."
 *
 * **Company-level, not under the job**, unlike almost everything else that
 * names a `jobId`. The two questions this module exists to answer —
 * *who is clocked in right now* and *what is waiting to be approved* —
 * both span every job, and a per-job collection would make each of them a
 * crawl across every job a company has. It is the same reasoning that put
 * `equipment` and `equipmentBookings` at the company level: where the
 * question ranges decides where the collection lives.
 *
 * **Instants are stored; minutes are derived.** Rounding at rest would
 * make the stored record disagree with the clock somebody watched, and
 * every later question — a time sheet, a labour cost, an argument about a
 * Friday — would inherit a rounding nobody could see. `workedMinutes`
 * rounds once, at the point of asking.
 */
export const SHIFT_SOURCES = ['clock', 'manual'] as const;
export const ShiftSourceSchema = z.enum(SHIFT_SOURCES);
export type ShiftSource = z.infer<typeof ShiftSourceSchema>;

export const SHIFT_SOURCE_LABEL: Record<ShiftSource, string> = {
  clock: 'Clocked',
  manual: 'Entered later',
};

/** At most this many breaks on one shift, so a document cannot grow unbounded. */
export const MAX_BREAKS = 20;

export const ShiftBreakSchema = z.object({
  id: IdSchema,
  startedAt: InstantSchema,
  /** Absent while the break is running. */
  endedAt: InstantSchema.optional(),
});
export type ShiftBreak = z.infer<typeof ShiftBreakSchema>;

/**
 * Who signed off a shift, and when (P8-08).
 *
 * **Present means approved**; there is no `status` field. A stored status
 * would be a second opinion about this object, and the two would part
 * company the first time a shift was edited after approval.
 */
export const ShiftApprovalSchema = z.object({
  at: InstantSchema,
  byUid: IdSchema,
});
export type ShiftApproval = z.infer<typeof ShiftApprovalSchema>;

export const ShiftSchema = AuditSchema.extend({
  id: IdSchema,
  companyId: IdSchema,
  jobId: IdSchema,
  /** Whose shift it is — not who recorded it. See `createdBy`. */
  uid: IdSchema,
  costCodeId: IdSchema.optional(),
  startedAt: InstantSchema,
  /** Absent while the shift is open. The field the "clocked in" query asks about. */
  endedAt: InstantSchema.optional(),
  breaks: z.array(ShiftBreakSchema).max(MAX_BREAKS).default([]),
  note: z.string().trim().max(2_000).default(''),
  /**
   * How it was recorded.
   *
   * **Kept because the two are not equally trustworthy**, and somebody
   * approving a week of time is entitled to know which is which. A
   * clocked shift was stamped by this system at the moment it happened; a
   * manual one is a person's recollection typed afterwards, which is both
   * legitimate and the thing to look at twice.
   */
  source: ShiftSourceSchema,
  /** Absent until somebody signs it off. See `ShiftApprovalSchema`. */
  approval: ShiftApprovalSchema.optional(),
  version: z.number().int().positive(),
});
export type Shift = z.infer<typeof ShiftSchema>;

export function isShiftOpen(shift: Pick<Shift, 'endedAt'>): boolean {
  return shift.endedAt === undefined;
}

export function isApproved(shift: Pick<Shift, 'approval'>): boolean {
  return shift.approval !== undefined;
}

/**
 * Waiting for somebody to sign it off.
 *
 * **An open shift is not waiting** — it is still being worked, and listing
 * it as outstanding would put a permanent item on the office's list every
 * morning that nobody can act on. Only a finished, unapproved shift is
 * asking for anything.
 */
export function needsApproval(shift: Pick<Shift, 'endedAt' | 'approval'>): boolean {
  return !isShiftOpen(shift) && !isApproved(shift);
}

/**
 * Whether the person who worked it is also the person who signed it off.
 *
 * Not refused, and not hidden. Refusing would stop a one-person builder
 * approving anything at all, which is a real company rather than an edge
 * case. But the point of approval is a second pair of eyes, so where there
 * was only one pair the screen says so rather than letting a self-approval
 * look like a reviewed one.
 */
export function isSelfApproved(shift: Pick<Shift, 'uid' | 'approval'>): boolean {
  return shift.approval !== undefined && shift.approval.byUid === shift.uid;
}

export function runningBreak(shift: Pick<Shift, 'breaks'>): ShiftBreak | null {
  return shift.breaks.find((one) => one.endedAt === undefined) ?? null;
}

const minutesBetween = (from: string, to: string): number =>
  Math.max(0, Math.round((Date.parse(to) - Date.parse(from)) / 60_000));

/**
 * Minutes on the clock, breaks taken off.
 *
 * `now` is passed rather than read, so an open shift's running total is a
 * pure function of its inputs — a clock read inside here would make every
 * test of it a test of the current time.
 *
 * A **running break is counted up to `now`** as well, because somebody
 * looking at a board wants the figure as it stands, not the figure as it
 * will be once they remember to come back.
 */
export function workedMinutes(
  shift: Pick<Shift, 'startedAt' | 'endedAt' | 'breaks'>,
  now: string,
): number {
  const end = shift.endedAt ?? now;
  const worked = minutesBetween(shift.startedAt, end);
  const paused = shift.breaks.reduce(
    (total, one) => total + minutesBetween(one.startedAt, one.endedAt ?? end),
    0,
  );
  // Never negative: a break recorded outside its shift is a data problem
  // to find in the list, not a reason to report minus forty minutes.
  return Math.max(0, worked - paused);
}

/** "7h 25m", because nobody reads a timesheet in minutes. */
export function formatMinutes(minutes: number): string {
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  if (hours === 0) return `${rest}m`;
  if (rest === 0) return `${hours}h`;
  return `${hours}h ${rest}m`;
}

/**
 * Whether two shifts for the same person overlap in time.
 *
 * An open shift counts as running to `now`: two open shifts always
 * overlap, which is the case this exists to refuse.
 */
export function shiftsOverlap(
  a: Pick<Shift, 'startedAt' | 'endedAt'>,
  b: Pick<Shift, 'startedAt' | 'endedAt'>,
  now: string,
): boolean {
  const aEnd = a.endedAt ?? now;
  const bEnd = b.endedAt ?? now;
  // Touching is not overlapping: clocking out of one job at 12:00 and into
  // the next at 12:00 is a normal day, and refusing it would make somebody
  // invent a minute.
  return a.startedAt < bEnd && b.startedAt < aEnd;
}
